import React, { useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { reset } from 'redux-form'
import { useDispatch, useSelector } from 'react-redux'
import { clearData } from 'redux/actions/data'
import Overlay from 'components/molecules/overlay'
import { translateForm } from 'utilities/form-utils'
import ComplexForm from 'components/molecules/complex-form'
import CarComments from 'components/redux-containers/car-comments-container'
import { media } from 'utilities/styled'

const ChildContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;

  ${media.desktop`
    flex-direction: row;
    align-items: stretch;
    gap: ${({ theme }) => theme.sizeByFactor(4)};
  `}
`

const CommentsSection = styled.div`
  flex: 1 1 auto;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;

  ${media.desktop`
    /* Align comments on the right side on desktop screens */
    order: 2;
    min-width: 0;
  `}
`

const StickyFormWrapper = styled.div`
  flex: 0 0 auto;
  background: ${({ theme }) => theme.colors.fieldBackground};
  z-index: 10;
  padding-top: 16px;
  padding-bottom: 24px;
  box-sizing: border-box;

  ${media.desktop`
    /* Align textarea input on the left side on desktop screens */
    order: 1;
    flex: 0 0 40%;
    background: transparent;
    padding-top: 0;
    padding-bottom: 0;
  `}
`

function CommentsOverlay({ addCarComment, closeOverlay, data, forms }) {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const overlayId = 'comment'
  const form = forms.comments.data.comment
  const formId = 'carFileComments'
  const translatedForm = translateForm(form.fieldsets, t)
  const commentsSectionRef = useRef(null)

  const comments = useSelector((state) => state.data.carComments)
  const commentsCount = comments?.data?.length || 0

  const scrollToBottom = () => {
    if (commentsSectionRef.current) {
      commentsSectionRef.current.scrollTop =
        commentsSectionRef.current.scrollHeight
    }
  }

  useEffect(() => {
    scrollToBottom()
  }, [commentsCount])

  const onClose = () => {
    dispatch(reset(formId))
    clearData('carComments')
    closeOverlay(overlayId)
  }

  const handleSubmit = (values) => {
    // Defensive: block empty/whitespace submissions. The form schema doesn't
    // always have `required: true` on the message field, so redux-form's
    // validation can let empty values through.
    const message = values?.message
    const isEmpty =
      message === null ||
      message === undefined ||
      message === '' ||
      (typeof message === 'string' && message.trim() === '')
    if (isEmpty) {
      return
    }
    addCarComment(values)
    dispatch(reset(formId))
    scrollToBottom()
  }

  return (
    <Overlay
      close={onClose}
      icon="comments"
      title={t('comments')}
      noPaddingBottom
    >
      <ChildContainer>
        <CommentsSection ref={commentsSectionRef}>
          <CarComments carId={data.auto_id} />
        </CommentsSection>
        <StickyFormWrapper>
          <ComplexForm
            data={data}
            fieldsets={translatedForm}
            formId={formId}
            onSubmit={handleSubmit}
            submitText={t('addComment')}
            level="cta"
            resetOnSubmit
            enableReinitialize
          />
        </StickyFormWrapper>
      </ChildContainer>
    </Overlay>
  )
}

CommentsOverlay.propTypes = {
  addCarComment: PropTypes.func.isRequired,
  closeOverlay: PropTypes.func.isRequired,
  data: PropTypes.object,
  forms: PropTypes.object,
}

CommentsOverlay.defaultProps = {
  data: null,
  forms: null,
}

export default CommentsOverlay
